import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import {
  getAPIURL,
  getVendorID,
  getFormattedPrice,
} from "../../services/helper";
import http from "../../services/http";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SalesTrends = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({
    summary: {},
    daily_data: [],
    chart_data: { labels: [], sales_counts: [], amounts: [] },
  });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 7))
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchSalesHistory();
  }, []);

  const fetchSalesHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        vendor_id: vendorId,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      const { data } = await http.get(
        `${api_url}/reports/sales/trends?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching sales trends:", error);
    }
    setLoading(false);
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <h2 className="text-center mb-4">Sales Trends</h2>

        {/* Date Range Filter */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Button
                  color="primary"
                  onClick={fetchSalesHistory}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Fetch Report"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {data.summary && (
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Sales</h5>
                  <h3>{data.summary.total_sales}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Amount</h5>
                  <h3>{getFormattedPrice(data.summary.total_amount)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <h5>Average Daily</h5>
                  <h3>{getFormattedPrice(data.summary.average_daily_amount)}</h3>
                  <small>({data.summary.average_daily_sales} sales)</small>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Peak and Lowest Day */}
        {data.summary.peak_day && (
          <Row className="mb-4">
            <Col md={6}>
              <Card className="text-center border-success">
                <CardBody>
                  <h5 className="text-success">Peak Day</h5>
                  <h4>{data.summary.peak_day.formatted_date}</h4>
                  <p>{getFormattedPrice(data.summary.peak_day.total_amount)} ({data.summary.peak_day.sales_count} sales)</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="text-center border-danger">
                <CardBody>
                  <h5 className="text-danger">Lowest Day</h5>
                  <h4>{data.summary.lowest_day.formatted_date}</h4>
                  <p>{getFormattedPrice(data.summary.lowest_day.total_amount)} ({data.summary.lowest_day.sales_count} sales)</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Chart */}
        {data.chart_data.labels.length > 0 && (
          <Card>
            <CardBody>
              <CardTitle tag="h5">Daily Sales Trend</CardTitle>
              {loading ? (
                <p>Loading...</p>
              ) : (
                <Line
                  data={{
                    labels: data.chart_data.labels,
                    datasets: [
                      {
                        label: 'Sales Amount',
                        data: data.chart_data.amounts,
                        borderColor: '#36A2EB',
                        backgroundColor: '#36A2EB',
                        tension: 0.1,
                        yAxisID: 'y'
                      },
                      {
                        label: 'Sales Count',
                        data: data.chart_data.sales_counts,
                        borderColor: '#FF6384',
                        backgroundColor: '#FF6384',
                        tension: 0.1,
                        yAxisID: 'y1'
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Daily Sales Trends'
                      }
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Amount ($)'
                        }
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Sales Count'
                        },
                        grid: {
                          drawOnChartArea: false,
                        },
                      }
                    }
                  }}
                />
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default SalesTrends;